import { ApplicationRef, ChangeDetectorRef, DestroyRef, Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

/**
 * Zoneless safety net: asks Angular to check every view, as zone.js did after each async task.
 *
 * Most of the app state still lives in plain fields of services and Eager components, which
 * Angular cannot see change on its own. Call refresh() where that state changes outside an
 * Angular event (template listener, async pipe, signal): API responses and realtime events
 * (done once in KFeathers), fetch(), FileReader, timers, document listeners.
 * New code should rather use signals, which need none of this.
 */
@Injectable({ providedIn: 'root' })
export class ViewRefresher {
    private appRef = inject(ApplicationRef);
    private dialog = inject(MatDialog);
    private watched = new Set<ChangeDetectorRef>();

    /**
     * For an OnPush page whose children keep plain fields: refresh() marks the root views only, and
     * Angular does not go down into an OnPush view that was not marked. Call it from the page's
     * injection context; the page is forgotten when destroyed.
     */
    watch(cd: ChangeDetectorRef): void {
        this.watched.add(cd);
        inject(DestroyRef).onDestroy(() => this.watched.delete(cd));
    }

    refresh(): void {
        // Without zone.js even ApplicationRef.tick() only checks the views marked for check, so
        // mark each root view: the app and the open dialogs (menus, selects and autocompletes
        // are rendered inside the app tree). Angular then runs a single check in a later task,
        // after the callers' await continuations, merged with any other notification
        this.appRef.components.forEach(component => component.changeDetectorRef.markForCheck());
        // openDialogs also lists the dialogs opened from lazy modules (they share the root list)
        this.dialog.openDialogs.forEach(dialogRef => dialogRef.componentRef?.changeDetectorRef.markForCheck());
        this.watched.forEach(cd => cd.markForCheck());
    }
}
